import { useCallback, useEffect, useMemo } from 'react';
import useOrderAttentionItemsQuery from '@/api/queries/useOrderAttentionItemsQuery';
import Button from '@/components/Button';
import NeedsAttentionStats from '@/components/dashboard/NeedsAttentionStats';
import PageHeader from '@/components/PageHeader';
import Pagination from '@/components/Pagination';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { SupplierHomePath } from '@/modules/supplier/paths';
import { useSearchParams } from 'react-router-dom';

const ITEMS_PER_PAGE = 10;

const Attention = () => {
  const auth = useAuth();
  const supplierId = auth.currentUser().supplierId;
  const supplierFilters = useMemo(() => (supplierId ? { supplierId } : undefined), [supplierId]);
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(Number(searchParams.get('page')) || 1, 1);
  const offset = (page - 1) * ITEMS_PER_PAGE;

  const {
    items,
    totalCount,
    estimatedTotalCount,
    isEstimatedTotalCount,
    isLoading,
    resolveIncompleteDelivery,
    isResolvingIncompleteDelivery,
  } = useOrderAttentionItemsQuery(supplierFilters, ITEMS_PER_PAGE, true, offset);

  const handlePageChange = useCallback(
    (nextPage: number) => {
      const params = new URLSearchParams();

      if (nextPage > 1) {
        params.set('page', String(nextPage));
      }

      setSearchParams(params);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (!isLoading && page > 1 && items.length === 0 && offset >= totalCount) {
      handlePageChange(Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE)));
    }
  }, [handlePageChange, isLoading, items.length, offset, page, totalCount]);

  return (
    <>
      <PageHeader title="Needs your attention">
        <Button to={SupplierHomePath} variant="secondary">
          Back to dashboard
        </Button>
      </PageHeader>

      {!isLoading && items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center text-sm text-slate-600">
          No orders need your attention.
        </div>
      ) : (
        <NeedsAttentionStats
          items={items}
          totalCount={totalCount}
          isEstimatedTotalCount={isEstimatedTotalCount}
          estimatedCountDisplayLimit={ITEMS_PER_PAGE}
          isLoading={isLoading}
          isResolving={isResolvingIncompleteDelivery}
          onResolveIncompleteDelivery={(orderId, incompleteDeliveryId) =>
            resolveIncompleteDelivery({ orderId, incompleteDeliveryId })
          }
        />
      )}

      {!isLoading && items.length > 0 && (
        <Pagination
          currentPage={page}
          itemsPerPage={ITEMS_PER_PAGE}
          totalCount={isEstimatedTotalCount ? undefined : totalCount}
          estimatedTotalCount={estimatedTotalCount}
          onClick={handlePageChange}
        />
      )}
    </>
  );
};

export default Attention;
